import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { integrationPaths } from "../paths";

export interface ConnectedIntegration {
  id: string;           // e.g. "slack"
  name: string;         // e.g. "Slack"
  instanceName?: string;// e.g. "Engineering Team"
  icon: string;
  color: string;
  status: "connected" | "error" | "expiring";
  connectedAt: string;
  pinned?: boolean;
}

// Nav template per provider id
export type NavSubItem = { label: string; path: string; iconName: string };
export const PROVIDER_NAV_TEMPLATES: Record<string, NavSubItem[]> = {
  slack: [
    { label: "Overview",      path: integrationPaths.slackOverview,   iconName: "LayoutDashboard" },
    { label: "Workspaces",    path: integrationPaths.slackWorkspaces, iconName: "Users"           },
    { label: "Channels",      path: integrationPaths.slackChannels, iconName: "Hash"            },
    { label: "Alert Routing", path: integrationPaths.slackRouting,  iconName: "Network"         },
    { label: "Templates",     path: integrationPaths.slackTemplates, iconName: "FileText"        },
    { label: "Delivery Logs", path: integrationPaths.slackLogs,      iconName: "Activity"        },
    { label: "Analytics",     path: integrationPaths.slackAnalytics, iconName: "BarChart3"       },
    { label: "Guide",         path: integrationPaths.slackHelp,    iconName: "BookOpen"        },
  ],
  teams: [
    { label: "Overview",      path: integrationPaths.notificationCenterComms, iconName: "LayoutDashboard" },
    { label: "Channels",      path: integrationPaths.notificationCenterComms, iconName: "Hash"            },
    { label: "Alert Routing", path: integrationPaths.notificationCenterRouting, iconName: "Network"         },
    { label: "Delivery Logs", path: integrationPaths.slackLogs,               iconName: "Activity"        },
    { label: "Analytics",     path: integrationPaths.slackAnalytics,          iconName: "BarChart3"       },
    { label: "Guide",         path: integrationPaths.slackHelp,               iconName: "BookOpen"        },
  ],
  jira: [
    { label: "Overview",      path: integrationPaths.notificationCenterTickets, iconName: "LayoutDashboard" },
    { label: "Projects",      path: integrationPaths.notificationCenterTickets, iconName: "FolderKanban"    },
    { label: "Issue Mapping", path: integrationPaths.notificationCenterTickets, iconName: "Target"          },
    { label: "Templates",     path: integrationPaths.slackTemplates,            iconName: "FileText"        },
    { label: "Automation",    path: integrationPaths.notificationCenterRouting, iconName: "Zap"             },
    { label: "Logs",          path: integrationPaths.slackLogs,                 iconName: "Activity"        },
    { label: "Analytics",     path: integrationPaths.slackAnalytics,          iconName: "BarChart3"       },
    { label: "Guide",         path: integrationPaths.slackHelp,                 iconName: "BookOpen"        },
  ],
  pagerduty: [
    { label: "Overview",      path: integrationPaths.notificationCenterComms, iconName: "LayoutDashboard" },
    { label: "Services",      path: integrationPaths.notificationCenterComms, iconName: "Layers"          },
    { label: "Escalations",   path: integrationPaths.notificationCenterRouting, iconName: "Network"         },
    { label: "Logs",          path: integrationPaths.slackLogs,               iconName: "Activity"        },
    { label: "Analytics",     path: integrationPaths.slackAnalytics,          iconName: "BarChart3"       },
    { label: "Guide",         path: integrationPaths.slackHelp,               iconName: "BookOpen"        },
  ],
  discord: [
    { label: "Overview",      path: integrationPaths.notificationCenterComms, iconName: "LayoutDashboard" },
    { label: "Servers",       path: integrationPaths.notificationCenterComms, iconName: "Hash"            },
    { label: "Alert Routing", path: integrationPaths.notificationCenterRouting, iconName: "Network"         },
    { label: "Logs",          path: integrationPaths.slackLogs,               iconName: "Activity"        },
    { label: "Guide",         path: integrationPaths.slackHelp,               iconName: "BookOpen"        },
  ],
  linear: [
    { label: "Overview",      path: integrationPaths.notificationCenterTickets, iconName: "LayoutDashboard" },
    { label: "Projects",      path: integrationPaths.notificationCenterTickets, iconName: "FolderKanban"    },
    { label: "Templates",     path: integrationPaths.slackTemplates,            iconName: "FileText"        },
    { label: "Logs",          path: integrationPaths.slackLogs,                 iconName: "Activity"        },
    { label: "Guide",         path: integrationPaths.slackHelp,                 iconName: "BookOpen"        },
  ],
  asana: [
    { label: "Overview",      path: integrationPaths.notificationCenterTickets, iconName: "LayoutDashboard" },
    { label: "Workspaces",    path: integrationPaths.notificationCenterTickets, iconName: "Users"           },
    { label: "Projects",      path: integrationPaths.notificationCenterTickets, iconName: "FolderKanban"    },
    { label: "Automation",    path: integrationPaths.notificationCenterRouting, iconName: "Zap"             },
    { label: "Logs",          path: integrationPaths.slackLogs,                 iconName: "Activity"        },
    { label: "Guide",         path: integrationPaths.slackHelp,                 iconName: "BookOpen"        },
  ],
  email: [
    { label: "Overview",      path: integrationPaths.notificationCenterComms, iconName: "LayoutDashboard" },
    { label: "Distribution",  path: integrationPaths.notificationCenterComms, iconName: "Users"           },
    { label: "Templates",     path: integrationPaths.slackTemplates,          iconName: "FileText"        },
    { label: "Delivery Logs", path: integrationPaths.slackLogs,               iconName: "Activity"        },
    { label: "Guide",         path: integrationPaths.slackHelp,               iconName: "BookOpen"        },
  ],
  webhook: [
    { label: "Overview",      path: integrationPaths.notificationCenterComms, iconName: "LayoutDashboard" },
    { label: "Endpoints",     path: integrationPaths.notificationCenterComms, iconName: "Link2"           },
    { label: "Delivery Logs", path: integrationPaths.slackLogs,                 iconName: "Activity"        },
    { label: "Guide",         path: integrationPaths.slackHelp,                 iconName: "BookOpen"        },
  ],
};

const DEFAULT_CONNECTED: ConnectedIntegration[] = [
  { id: "slack",     name: "Slack",            icon: "💬", color: "#E01E5A", status: "connected", connectedAt: "2026-01-10", pinned: true },
  { id: "teams",     name: "Microsoft Teams",  icon: "🏢", color: "#5059C9", status: "connected", connectedAt: "2026-01-12" },
  { id: "pagerduty", name: "PagerDuty",        icon: "🚨", color: "#06AC38", status: "expiring",  connectedAt: "2026-01-15" },
  { id: "jira",      name: "Jira",             icon: "🎯", color: "#2684FF", status: "connected", connectedAt: "2026-01-20" },
  { id: "discord",   name: "Discord",          icon: "🎮", color: "#5865F2", status: "connected", connectedAt: "2026-02-01" },
  { id: "linear",    name: "Linear",           icon: "◆",  color: "#5E6AD2", status: "connected", connectedAt: "2026-02-10" },
];

interface IntegrationsContextType {
  integrations: ConnectedIntegration[];
  addIntegration: (intg: ConnectedIntegration) => void;
  removeIntegration: (id: string) => void;
  togglePin: (id: string) => void;
}

const IntegrationsContext = createContext<IntegrationsContextType>({
  integrations: DEFAULT_CONNECTED,
  addIntegration: () => {},
  removeIntegration: () => {},
  togglePin: () => {},
});

export function IntegrationsProvider({ children }: { children: ReactNode }) {
  const [integrations, setIntegrations] = useState<ConnectedIntegration[]>(() => {
    try {
      const stored = localStorage.getItem("cm_integrations");
      return stored ? JSON.parse(stored) : DEFAULT_CONNECTED;
    } catch {
      return DEFAULT_CONNECTED;
    }
  });

  useEffect(() => {
    localStorage.setItem("cm_integrations", JSON.stringify(integrations));
  }, [integrations]);

  const addIntegration = (intg: ConnectedIntegration) => {
    setIntegrations((prev) => {
      if (prev.find((i) => i.id === intg.id)) return prev;
      return [...prev, intg];
    });
  };

  const removeIntegration = (id: string) => {
    setIntegrations((prev) => prev.filter((i) => i.id !== id));
  };

  const togglePin = (id: string) => {
    setIntegrations((prev) =>
      prev.map((i) => (i.id === id ? { ...i, pinned: !i.pinned } : i))
    );
  };

  return (
    <IntegrationsContext.Provider value={{ integrations, addIntegration, removeIntegration, togglePin }}>
      {children}
    </IntegrationsContext.Provider>
  );
}

export const useIntegrations = () => useContext(IntegrationsContext);
